import { useMemo, useState } from 'react'
import clsx from 'clsx'
import { Icon } from '@iconify/react'
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault,
} from 'use-query-params'
import {
  TabPane,
  Tabs,
  useDebounceState,
  useGetGapReports,
  useTopbarComponent,
  useContentRefreshDomains,
} from '@pepper/ui'

import PropertySelectorModal from 'components/PropertySelectorModal'

import { useSelectedCompanyId } from 'utils/functions/misc'
import { CONTENT_REFRESH_TABS } from './constants'
import DomainRefresh from './DomainRefresh'
import UrlRefresh from './UrlRefresh'

import s from './ContentRefresh.module.css'

type ActiveTabs = 'domain_refresh' | 'url_refresh'
type GeneratedReportsTab = 'past_reports'
type AllTabs = ActiveTabs | GeneratedReportsTab

const DEBOUNCE_TIME = 800
const PAGE_SIZE = 25
const REFETCH_INTERVAL = 20000

const ContentRefresh = (): JSX.Element => {
  const [activeTab, setActiveTab] = useQueryParam(
    'tab',
    withDefault(StringParam, 'domain_refresh')
  )
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 1))
  const companyId = useSelectedCompanyId()
  const [urlSearch, setUrlSearch] = useState('')
  const debouncedSearchValue = useDebounceState<string>(
    urlSearch,
    DEBOUNCE_TIME
  )
  const {
    connectedGoogleAccount,
    data: domains,
    refetch,
  } = useContentRefreshDomains(companyId)

  const { reports, isLoading } = useGetGapReports({
    companyId,
    isEnabled: !!companyId,
    page,
    limit: PAGE_SIZE,
    pageUrl: debouncedSearchValue,
    refetchInterval: activeTab === 'domain_refresh' ? false : REFETCH_INTERVAL,
  })

  const handleTabClick = (activeTabKey: AllTabs) => {
    setActiveTab(activeTabKey)
    setPage(undefined)
  }

  const title = useMemo(() => {
    return (
      <div className={s.headerWrapper}>
        <span>Website Content Audit</span>
      </div>
    )
  }, [])

  useTopbarComponent({
    header: title,
  })

  return (
    <div className={s.landingWrapper}>
      {!!connectedGoogleAccount && (
        <PropertySelectorModal
          domains={domains}
          connectedAccount={connectedGoogleAccount}
          companyId={companyId}
          onSuccess={refetch}
          onDisconnect={refetch}
        />
      )}
      <div className={s.tabsWrapper}>
        <Tabs
          onTabClick={tabKey => handleTabClick(tabKey as ActiveTabs)}
          tabBarExtraContent={
            <button
              onClick={() => handleTabClick('past_reports')}
              className={clsx(s.generatedReports, {
                [s.active]: activeTab === 'past_reports',
              })}
            >
              <Icon icon="lucide:file" /> Past Reports{' '}
              <div className={s.reports}>{reports?.total_records ?? 0}</div>
            </button>
          }
          defaultActiveKey={activeTab}
          activeKey={activeTab}
        >
          {CONTENT_REFRESH_TABS.map(tab => (
            <TabPane
              tab={
                <div className={s.tabPane} data-id={tab.dataId}>
                  {tab.icon} {tab.label}
                </div>
              }
              tabKey={tab.value}
              key={tab.value}
              active={true}
            >
              {tab.value === 'domain_refresh' && <DomainRefresh />}
              {tab.value === 'url_refresh' && (
                <UrlRefresh
                  dataSource={reports?.data ?? []}
                  isLoading={isLoading}
                  isGeneratedReportsTab={false}
                  setPage={setPage}
                  limit={PAGE_SIZE}
                  totalRecords={reports?.total_records ?? 0}
                  setUrlSearch={setUrlSearch}
                  page={page}
                />
              )}
            </TabPane>
          ))}
        </Tabs>
      </div>
      {activeTab === 'past_reports' && (
        <UrlRefresh
          dataSource={reports?.data ?? []}
          isGeneratedReportsTab={activeTab === 'past_reports'}
          isLoading={isLoading}
          setPage={setPage}
          limit={PAGE_SIZE}
          totalRecords={reports?.total_records ?? 0}
          setUrlSearch={setUrlSearch}
          page={page}
        />
      )}
    </div>
  )
}
export default ContentRefresh
